"use client";

import { STATS, SUCCESS_RATE } from "@/lib/stats";
import { useState } from "react";
import Breadcrumb from "@/components/Breadcrumb";
import Avatar from "@/components/Avatar";
import VideoTestimonial from "@/components/VideoTestimonial";
import Link from "next/link";
import {
  Users,
  GraduationCap,
  Heart,
  Handshake,
  MessageCircle,
  Quote,
  Rocket,
  Star,
} from "lucide-react";
import testimonialsDataJson from "@/data/testimonials.json";

type TestimonialCategory = "all" | "students" | "parents" | "volunteers";

interface Testimonial {
  id: number;
  name: string;
  role: string;
  category: Exclude<TestimonialCategory, "all">;
  avatar: string;
  rating: number;
  content: string;
  result?: {
    label: string;
    value: string;
  };
}

const testimonials: Testimonial[] = testimonialsDataJson.testimonials.map(
  (t) => ({
    ...t,
    category: t.category as Exclude<TestimonialCategory, "all">,
  })
);

const iconMap = {
  Users: Users,
  GraduationCap: GraduationCap,
  Heart: Heart,
  Handshake: Handshake,
};

const categories = testimonialsDataJson.categories.map((cat) => ({
  id: cat.id as TestimonialCategory,
  label: cat.label,
  icon: cat.icon,
}));

export default function TestimonialsPage() {
  const [activeCategory, setActiveCategory] =
    useState<TestimonialCategory>("all");

  const filteredTestimonials = testimonials.filter(
    (testimonial) =>
      activeCategory === "all" || testimonial.category === activeCategory
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <Breadcrumb pathname="/testimonials" title="Témoignages" />
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-c2e-blue to-c2e-red text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Ils ont réussi avec C2E
            </h1>
            <p className="text-xl text-white/90">
              Découvrez les histoires inspirantes de nos membres qui ont atteint
              l'excellence académique
            </p>
          </div>
        </div>
      </div>

      {/* Success Stats */}
      <div className="container mx-auto px-4 -mt-8 relative z-10">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-c2e-blue mb-2">
                {SUCCESS_RATE.value}
              </div>
              <div className="text-gray-600 font-medium text-sm md:text-base">
                Taux de réussite aux examens
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-c2e-blue mb-2">
                {STATS.satisfaction}
              </div>
              <div className="text-gray-600 font-medium text-sm md:text-base">
                Note moyenne de satisfaction
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-c2e-blue mb-2">
                {STATS.reviewsCount}
              </div>
              <div className="text-gray-600 font-medium text-sm md:text-base">
                Avis recueillis
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-c2e-blue mb-2">
                {STATS.students}
              </div>
              <div className="text-gray-600 font-medium text-sm md:text-base">
                Élèves accompagnés
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-wrap justify-center gap-3">
          {categories.map((cat) => {
            const IconComponent = iconMap[cat.icon as keyof typeof iconMap];
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all flex items-center gap-2 ${
                  activeCategory === cat.id
                    ? "bg-c2e-blue text-white shadow-lg scale-105"
                    : "bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200"
                }`}
              >
                {IconComponent && <IconComponent className="h-5 w-5" />}
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Testimonials Grid */}
      <div className="container mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {filteredTestimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-white rounded-2xl shadow-sm border-2 border-transparent hover:border-c2e-blue hover:shadow-xl transition-all p-6 flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center gap-4 mb-4">
                <Avatar
                  src=""
                  alt={testimonial.name}
                  size={70}
                  className="border-3 border-c2e-blue"
                />
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">
                    {testimonial.name}
                  </h3>
                  <p className="text-c2e-blue text-sm font-medium">
                    {testimonial.role}
                  </p>
                </div>
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star
                    key={i}
                    className="h-5 w-5 text-yellow-400 fill-current"
                    aria-hidden="true"
                  />
                ))}
                <span className="sr-only">
                  Note : {testimonial.rating} sur 5
                </span>
              </div>

              {/* Content */}
              <div className="mb-4 flex-grow">
                <div className="relative">
                  <Quote
                    className="absolute -top-2 -left-2 h-8 w-8 text-c2e-blue/20 fill-current rotate-180"
                    aria-hidden="true"
                  />
                  <p className="text-gray-700 leading-relaxed italic pl-6">
                    {testimonial.content}
                  </p>
                </div>
              </div>

              {/* Result */}
              {testimonial.result && (
                <div className="bg-gray-50 rounded-xl p-4 text-center border-l-4 border-c2e-blue">
                  <p className="text-xs text-gray-500 mb-1">
                    {testimonial.result.label}
                  </p>
                  <p className="text-sm font-bold text-c2e-blue">
                    {testimonial.result.value}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Video Testimonials Section */}
      <div className="bg-white py-12 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">
              Témoignages Vidéo
            </h2>
            <p className="text-center text-gray-600 mb-8">
              Écoutez directement nos membres partager leur expérience
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonialsDataJson.videos.map((video) => (
                <VideoTestimonial
                  key={video.id}
                  videoUrl={video.videoUrl}
                  title={video.title}
                  author={video.author}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-c2e-blue to-c2e-red rounded-3xl p-8 md:p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">
            Rejoignez nos Success Stories
          </h3>
          <p className="text-xl text-white/90 mb-8">
            Vous aussi, écrivez votre histoire de réussite avec C2E
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/join"
              className="px-8 py-4 bg-white text-c2e-blue rounded-full font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Rocket className="h-5 w-5" aria-hidden="true" />
              Commencer Maintenant
            </Link>
            {/* <Link
              href="/contact"
              className="px-8 py-4 border-2 border-white text-white rounded-full font-semibold hover:bg-white hover:text-c2e-blue transition-colors inline-flex items-center justify-center gap-2"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              Témoigner
            </Link> */}
            <a
              href="https://forms.gle/faH1UG4UbKe6nUn46" // lien Google Form
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 border-2 border-white text-white rounded-full font-semibold hover:bg-white hover:text-c2e-blue transition-colors inline-flex items-center justify-center gap-2"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              Donner mon avis
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
